import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {
    LearnExperienceFrameDockSide,
    LearnExperienceState
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/LearnExperienceState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DragGhostDirection, ResizeEvent, ResizePhase, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {VisualDesignerBody} from '@mintdata-wsp/wsp/ui/views/visualDesigner/body/VisualDesignerBody';
import {KeyboardShortcuts} from '@mintdata-wsp/wsp/ui/views/visualDesigner/container/KeyboardShortcuts';
import {VisualDesignerHeader} from '@mintdata-wsp/wsp/ui/views/visualDesigner/header/VisualDesignerHeader';
import {LearnExperience} from '@mintdata-wsp/wsp/ui/views/visualDesigner/learnExperience/LearnExperience';
import {VersionsPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/versions/VersionsPanel';
import {VersionsView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/versions/VersionsView';
import {TopBarDotButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarDotButton';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./VisualDesignerContainer.less');
const cx = classnames.bind(styles);

@observer
export class VisualDesignerContainer extends React.Component<any, any>
{
    @observable
    private _resizerOffset = 0;

    private _getRootWorkbook():Workbook
    {
        const openDesign = controllers.ui.openDesign.getOpenDesign();

        if( !openDesign || !openDesign.designState || !openDesign.designState.spreadsheets )
            return null;

        return openDesign.designState.spreadsheets.rootWorkbook;
    }

    @action.bound
    private _handleResize(event:ResizeEvent):void
    {
        const {lx} = controllers.ui;
        const lxState:LearnExperienceState = lx.getState();

        if( event.phase === ResizePhase.Start )
        {
            const clientRect:ClientRect = lxState.iframeEl.getBoundingClientRect();

            this._resizerOffset = lxState.dockSide === LearnExperienceFrameDockSide.Left
                ? clientRect.width
                : lxState.dockSide === LearnExperienceFrameDockSide.Right
                    ? window.innerWidth - clientRect.width
                    : lxState.dockSide === LearnExperienceFrameDockSide.Top
                        ? clientRect.height
                        : window.innerHeight - clientRect.height;
        }

        this._resizerOffset += lxState.dockSide === LearnExperienceFrameDockSide.Left
        || lxState.dockSide === LearnExperienceFrameDockSide.Right
            ? event.deltaX
            : event.deltaY;

        if( event.phase === ResizePhase.Stop )
        {
            lxState.dockSize = lxState.dockSide === LearnExperienceFrameDockSide.Left
            || lxState.dockSide === LearnExperienceFrameDockSide.Top
                ? this._getResizerOffset()
                : lxState.dockSide === LearnExperienceFrameDockSide.Right
                    ? window.innerWidth - this._getResizerOffset()
                    : window.innerHeight - this._getResizerOffset();

            this._resizerOffset = 0;
        }
    }

    private _getResizerOffset():number
    {
        const {lx} = controllers.ui;
        const lxState:LearnExperienceState = lx.getState();

        const clientSize:number = lxState.dockSide === LearnExperienceFrameDockSide.Left
        || lxState.dockSide === LearnExperienceFrameDockSide.Right
            ? window.innerWidth
            : window.innerHeight;

        return Math.max(Math.min(this._resizerOffset, clientSize - 30), 30);
    }

    render():JSX.Element
    {
        const rootWorkbook = this._getRootWorkbook();

        if( !rootWorkbook ) return null;

        const {ui: {visualDesigner: {lx: {dockSide, isVisible}}}} = rootState;
        const {openDesign} = controllers.ui;

        const isSampleDesign:boolean = openDesign.isInSampleDesignMode();
        const isVersionsPanelVisible = controllers.ui.designVersions.isVersionsPanelVisible();
        const isDiffVisible = controllers.ui.designVersions.isDiffVisible();
        const isDetailsVisible = controllers.ui.designVersions.isDetailsVisible();
        const isShowVersionsView = isDiffVisible || isDetailsVisible;

        return (
            <KeyboardShortcuts>
                <div className={cx('base', dockSide, {noPointerEvents: this._resizerOffset !== 0})}>

                    <div className={cx('mainWrapper')}>
                        <div className={cx('main')}>
                            <VisualDesignerHeader />
                            <VisualDesignerBody />
                            {!isSampleDesign && <TopBarDotButton />}
                        </div>

                        {isShowVersionsView && <VersionsView />}

                        <div className={cx('sidebar', {isVisible: isVersionsPanelVisible})}>
                            {isVersionsPanelVisible && <VersionsPanel />}
                        </div>
                    </div>

                    <Resizer
                        isVisible={isVisible && dockSide !== LearnExperienceFrameDockSide.Floating}
                        isVertical={
                            dockSide == LearnExperienceFrameDockSide.Left
                            || dockSide === LearnExperienceFrameDockSide.Right
                        }
                        className={cx('resizer')}
                        dragGhostDirection={
                            dockSide == LearnExperienceFrameDockSide.Left
                            || dockSide === LearnExperienceFrameDockSide.Right
                                ? DragGhostDirection.Left
                                : DragGhostDirection.Top
                        }
                        dragGhostOffset={this._getResizerOffset()}
                        onResize={this._handleResize}
                    />
                    <LearnExperience />


                </div>
            </KeyboardShortcuts>
        );
    }
}
